(() => {
  'use strict';

  const DISCLOSURE_SELECTOR = '[data-womuse-mto-disclosure]';
  const INVENTORY_SELECTOR = '[data-womuse-inventory-wrapper]';

  const mobileQuery = window.matchMedia('(max-width: 749px)');

  /*
   * WOMUSE — Made-to-Order Disclosure
   */

  function initialiseDisclosure(disclosure) {
    if (disclosure.dataset.mtoDisclosureInitialised === 'true') return;

    const trigger = disclosure.querySelector(
      '[data-womuse-mto-trigger]'
    );

    const panel = disclosure.querySelector(
      '[data-womuse-mto-panel]'
    );

    const closeButton = disclosure.querySelector(
      '[data-womuse-mto-close]'
    );

    const wash = disclosure.querySelector(
      '[data-womuse-mto-wash]'
    );

    const journey = disclosure.querySelector(
      '[data-womuse-mto-journey]'
    );

    if (!trigger || !panel) return;

    disclosure.dataset.mtoDisclosureInitialised = 'true';

    const steps = journey
      ? Array.from(
          journey.querySelectorAll('.womuse-mto-journey__step')
        )
      : [];

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    let animationPlayed = false;

    function lockMobilePage() {
      if (!mobileQuery.matches) return;

      document.documentElement.classList.add(
        'womuse-mto-sheet-open'
      );
    }

    function unlockMobilePage() {
      document.documentElement.classList.remove(
        'womuse-mto-sheet-open'
      );
    }

    function completeJourneyImmediately() {
      if (!journey) return;

      steps.forEach((step) => {
        step.classList.add('is-complete');
      });

      journey.classList.remove('is-animating');
      journey.classList.add('is-complete');
    }

    function playJourney() {
      if (!journey || animationPlayed) return;

      animationPlayed = true;

      if (reducedMotion) {
        completeJourneyImmediately();
        return;
      }

      journey.classList.add('is-animating');

      const delays = [100, 650, 1200, 1750];

      steps.forEach((step, index) => {
        window.setTimeout(() => {
          step.classList.add('is-complete');

          if (index === steps.length - 1) {
            journey.classList.remove('is-animating');
            journey.classList.add('is-complete');
          }
        }, delays[index] || 1750);
      });
    }

    function open() {
      disclosure.classList.add('is-open');

      panel.hidden = false;

      trigger.setAttribute('aria-expanded', 'true');

      if (wash && mobileQuery.matches) {
        wash.hidden = false;
      }

      lockMobilePage();

      window.requestAnimationFrame(playJourney);
    }

    function close() {
      disclosure.classList.remove('is-open');

      panel.hidden = true;

      trigger.setAttribute('aria-expanded', 'false');

      if (wash) {
        wash.hidden = true;
      }

      unlockMobilePage();
    }

    trigger.addEventListener('click', () => {
      const isOpen =
        trigger.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        close();
      } else {
        open();
      }
    });

    if (closeButton) {
      closeButton.addEventListener('click', close);
    }

    if (wash) {
      wash.addEventListener('click', close);
    }

    document.addEventListener('keydown', (event) => {
      if (
        event.key === 'Escape' &&
        trigger.getAttribute('aria-expanded') === 'true'
      ) {
        close();
        trigger.focus();
      }
    });

    mobileQuery.addEventListener('change', () => {
      if (!mobileQuery.matches) {
        if (wash) {
          wash.hidden = true;
        }

        unlockMobilePage();
      } else if (
        trigger.getAttribute('aria-expanded') === 'true'
      ) {
        if (wash) {
          wash.hidden = false;
        }

        lockMobilePage();
      }
    });

    // Allows inventory switching to close the journey.
    disclosure.womuseClose = close;
  }

  /*
   * WOMUSE — Country Selector
   */

  function initialiseCountrySelector(root) {
    root
      .querySelectorAll('[data-womuse-mto-country]')
      .forEach((countrySelector) => {
        if (
          countrySelector.dataset.mtoCountryInitialised === 'true'
        ) {
          return;
        }

        const toggle = countrySelector.querySelector(
          '[data-womuse-mto-country-toggle]'
        );

        const menu = countrySelector.querySelector(
          '[data-womuse-mto-country-menu]'
        );

        if (!toggle || !menu) return;

        countrySelector.dataset.mtoCountryInitialised = 'true';

        function closeCountry() {
          menu.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
        }

        toggle.addEventListener('click', (event) => {
          event.stopPropagation();

          const shouldOpen = menu.hidden;

          menu.hidden = !shouldOpen;

          toggle.setAttribute(
            'aria-expanded',
            String(shouldOpen)
          );
        });

        document.addEventListener('click', (event) => {
          if (!countrySelector.contains(event.target)) {
            closeCountry();
          }
        });

        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            closeCountry();
          }
        });
      });
  }

  /*
   * WOMUSE — Inventory Data
   */

  function getInventoryData(wrapper) {
    if (wrapper.womuseInventoryData) {
      return wrapper.womuseInventoryData;
    }

    const jsonElement = wrapper.querySelector(
      '[data-product-inventory-json]'
    );

    if (!jsonElement) return null;

    try {
      const data = JSON.parse(jsonElement.textContent);

      wrapper.womuseInventoryData = data.inventory || {};

      return wrapper.womuseInventoryData;
    } catch (error) {
      console.warn(
        'WOMUSE: Unable to read inventory data.',
        error
      );

      return null;
    }
  }

  /*
   * WOMUSE — Update Inventory Display
   */

  function updateInventoryStatus(wrapper, variantId) {
    if (!wrapper || !variantId) return;

    const mtoElement = wrapper.querySelector(
      '[data-womuse-inventory-mto]'
    );

    const nativeElement = wrapper.querySelector(
      '[data-womuse-inventory-native]'
    );

    if (!mtoElement || !nativeElement) return;

    const inventoryData = getInventoryData(wrapper);

    if (!inventoryData) return;

    const variantInventory =
      inventoryData[String(variantId)];

    if (!variantInventory) return;

    const excluded =
      mtoElement.dataset.womuseMtoExcluded === 'true';

    const isMTO =
      !excluded &&
      variantInventory.inventory_policy === 'continue' &&
      Number(variantInventory.inventory_quantity) < 1;

    if (!isMTO) {
      const disclosure = mtoElement.querySelector(
        DISCLOSURE_SELECTOR
      );

      if (
        disclosure &&
        disclosure.classList.contains('is-open')
      ) {
        if (typeof disclosure.womuseClose === 'function') {
          disclosure.womuseClose();
        }
      }
    }

    mtoElement.hidden = !isMTO;
    nativeElement.hidden = isMTO;
  }

  /*
   * WOMUSE — Read Selected Variant
   *
   * Stiletto updates the product form's variant ID
   * and dispatches a change event.
   */

  function getSelectedVariantId(form) {
    if (!form) return null;

    const variantInput = form.querySelector(
      '[name="id"]'
    );

    if (!variantInput) return null;

    const variantId = Number(variantInput.value);

    return Number.isFinite(variantId) && variantId > 0
      ? variantId
      : null;
  }

  /*
   * WOMUSE — Find Product Inventory Wrapper
   */

  function getProductWrapper(form) {
  if (!form) return null;

  const productContainer = form.closest(
    '[data-product-handle]'
  );

  if (!productContainer) return null;

  // Find the inventory component within the same Shopify section.
  const section = productContainer.closest(
    '.shopify-section'
  );

  if (!section) return null;

  return section.querySelector(
    INVENTORY_SELECTOR
  );
}

  /*
   * WOMUSE — Sync From Product Form
   */

  function syncFromProductForm(form) {
    if (!form) return;

    const wrapper = getProductWrapper(form);

    if (!wrapper) return;

    const variantId = getSelectedVariantId(form);

    if (!variantId) return;

    updateInventoryStatus(wrapper, variantId);
  }

  /*
   * WOMUSE — Listen for Variant Changes
   *
   * Stiletto dispatches a change event on the product
   * form after updating its selected variant.
   *
   * We also listen for native option changes and
   * schedule the update after Stiletto has processed them.
   */

  function handleFormChange(event) {
  const form = event.currentTarget;

  if (!form) return;

  const section = form.closest('.shopify-section');
  if (!section) return;

  const wrapper = section.querySelector(
    '[data-womuse-inventory-wrapper]'
  );

  if (!wrapper) return;

  requestAnimationFrame(() => {
    const variantId = form.querySelector('[name="id"]')?.value;

    if (!variantId) return;

    updateInventoryStatus(wrapper, variantId);
  });
}

  function initialiseVariantListeners(root = document) {
  const sections = root.matches?.('.shopify-section')
    ? [root]
    : Array.from(root.querySelectorAll('.shopify-section'));

  sections.forEach((section) => {
    const wrapper = section.querySelector(
      '[data-womuse-inventory-wrapper]'
    );

    if (!wrapper) return;

    const form = section.querySelector(
      'form:has([name="id"])'
    );

    if (!form || form.dataset.womuseVariantInitialised === 'true') {
      return;
    }

    form.dataset.womuseVariantInitialised = 'true';

    form.addEventListener('change', handleFormChange);
  });
}

  /*
   * WOMUSE — Initialise
   */

  function initialiseAll(root = document) {
    root
      .querySelectorAll(DISCLOSURE_SELECTOR)
      .forEach(initialiseDisclosure);

    initialiseCountrySelector(root);
  }

  initialiseAll();
initialiseVariantListeners();

document.addEventListener(
  'shopify:section:load',
  (event) => {
    initialiseAll(event.target);
    initialiseVariantListeners(event.target);
  }
);

})();